/**
 * Type guard for undefined types
 */
export function isDefined<T>(v?: T): v is T {
  return !(typeof v === 'undefined');
}

export function isTruthy<T>(v?: T): v is NonNullable<Exclude<T, false>> {
  return Boolean(typeof v !== 'undefined' && v !== null && v);
}

export function isNotNull<T>(v?: T): v is Exclude<T, null> {
  return v !== null;
}

/**
 * Turns this:
 * ```ts
 * const x = y === undefined ? foo(y) : z;
 * ```
 *
 * Into this:
 * ```ts
 * const x = ifDefined(foo)(y) ?? z;
 * ```
 *
 * Allows partial application, for re-use:
 *  ```ts
 *   const stringIfDef = ifDefined((num: number) => `${num}`);
 *   return {
 *     a: stringIfDef(numberA) ?? defaults.stringA,
 *     b: stringIfDef(numberB) ?? defaults.stringB,
 *   }
 *   ```
 */
export const ifDefined: IfDefinedType = (fn) => (value) => (typeof value === 'undefined' ? value : fn(value));
type IfDefinedType = <V, R>(fn: (value: V) => R) => (value: V | undefined) => R | undefined;

export const makeTuple = <T extends (string | number | symbol | null)[]>(...args: T) => args;

// Extract sort field and order while maintaining their types
type SortOrder = 'asc' | 'dsc';
export function splitSort<Field extends string>(sort: `${Field}:${SortOrder}`): [Field, SortOrder] {
  return sort.split(':') as never;
}
